import type { CruiseOnlyOfferView } from 'hooks/Cruise/useCruiseOfferView'
import type { MouseEvent } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import productLabels from 'i18n/productLabels'
import Group from 'components/utils/Group'
import CruiseBundleSavingsPill from 'components/Cruises/Common/CruiseBundleSavingsPill'
import LineBedDoubleIcon from 'LuxKit/Icons/line/LineBedDoubleIcon'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import { _searchTileContainerQueryUp } from '../_SearchTileBase'
import SearchTileRowInclusionItem from '../shared/SearchTileRowInclusionItem'
import CruiseSearchTileFeatures from './CruiseSearchTileFeatures'
import CruiseSearchTilePricing from './CruiseSearchTilePricing'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import useIsOpenNewTabOfferClickEnabled from 'hooks/Offers/useIsOpenNewTabOfferClickEnabled'
import { CRUISE_BUNDLE_FILTER_QUERY_PARAM } from 'constants/cruise'
import UrlLocation from 'src/lib/url/UrlLocation'
import OfferUrlContext from 'contexts/OfferUrlContext'
import GeoContext from 'contexts/geoContext'
import useNavigateTo from 'hooks/useNavigateTo'
import { useLocaleUrlOptions } from 'hooks/useLocaleUrlOptions'
import { buildUrl } from 'lib/url/searchUrlUtils'

const Cards = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${rem(16)};

  ${_searchTileContainerQueryUp.tablet} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`

const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};
  min-width: 0;
  padding: ${rem(16)};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.S};
`

const CardContent = styled.div`
  flex: 1;
`

interface Props {
  view: CruiseOnlyOfferView
  filters?: App.OfferListFilters
  viewOfferLabel: string
}

function CruiseSearchTileBundleCards({ view, filters, viewOfferLabel }: Props) {
  const intl = useIntl()
  const offerUrl = useOfferUrl(view.offer, { filters, offerLinkIncludesFilters: true })
  const parentOfferUrl = useContext(OfferUrlContext)
  const { currentRegionCode } = useContext(GeoContext)
  const localeUrlOptions = useLocaleUrlOptions()
  const navigateTo = useNavigateTo()
  const isOpenNewTabOfferClickEnabled = useIsOpenNewTabOfferClickEnabled()
  const bundleUrl = useMemo(() => {
    const url = UrlLocation.fromUrl(parentOfferUrl ?? offerUrl)
    const params = new URLSearchParams(url.query)
    params.set(CRUISE_BUNDLE_FILTER_QUERY_PARAM, 'true')
    return buildUrl(url.path, currentRegionCode, params.toString(), url.location, localeUrlOptions)
  }, [parentOfferUrl, offerUrl, currentRegionCode, localeUrlOptions])
  const onViewBundleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault()
    event.stopPropagation()
    if (isOpenNewTabOfferClickEnabled || event.metaKey || event.ctrlKey || event.shiftKey) {
      window.open(bundleUrl, '_blank', 'noopener')
    } else {
      navigateTo(bundleUrl)
    }
  }, [bundleUrl, isOpenNewTabOfferClickEnabled, navigateTo])
  const bundleTitle = intl.formatMessage({
    id: 'TImIe02foM_Ykxzivi0V',
    defaultMessage: 'Cruise & Hotel bundle',
    description: 'Heading for the combined cruise and hotel price card in cruise search results',
  })
  const viewBundleLabel = intl.formatMessage({
    id: 'VKfuja2E4NoTkld-Whih',
    defaultMessage: 'View bundle',
    description: 'Button text to view the combined cruise and hotel offer from cruise search results',
  })
  const hotelStayLabel = intl.formatMessage({
    id: 'ISJCGT2oD0EBw5ZytmAD',
    defaultMessage: 'Hotel stay for 2 adults',
    description: 'Highlighted inclusion for the hotel stay included in the cruise and hotel search card',
  })
  const hotelInclusion = useMemo(() => ({
    id: 'cruise-bundle-hotel-stay',
    description: hotelStayLabel,
    isHighlighted: true,
    Icon: LineBedDoubleIcon,
  }), [hotelStayLabel])
  const bundleView = useMemo<CruiseOnlyOfferView | undefined>(() => view.lowestBundlePrice ? {
    ...view,
    lowestPrice: view.lowestBundlePrice,
    memberLowestPrice: undefined,
    memberPriceType: undefined,
    discountPills: undefined,
  } : undefined, [view])
  const standardView = useMemo<CruiseOnlyOfferView>(() => ({
    ...view,
    discountPills: undefined,
  }), [view])

  if (!bundleView) return null

  return <Group direction="vertical" gap={16}>
    <CruiseBundleSavingsPill discount={view.bundleDiscountPills} />
    <Cards>
      <Card>
        <CardContent>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading5" as="h3">{intl.formatMessage(productLabels.cruise)}</Heading>
            <CruiseSearchTileFeatures view={view} filters={filters} />
          </Group>
        </CardContent>
        <CruiseSearchTilePricing view={standardView} />
        <TextButton nonInteractable kind="primary" fit="full-width">{viewOfferLabel}</TextButton>
      </Card>
      <Card>
        <CardContent>
          <Group direction="vertical" gap={12}>
            <Heading variant="heading5" as="h3">{bundleTitle}</Heading>
            <SearchTileRowInclusionItem inclusion={hotelInclusion} />
            <CruiseSearchTileFeatures view={view} filters={filters} />
          </Group>
        </CardContent>
        <CruiseSearchTilePricing view={bundleView} />
        <TextButton
          kind="primary"
          fit="full-width"
          onClick={onViewBundleClick}
        >
          {viewBundleLabel}
        </TextButton>
      </Card>
    </Cards>
  </Group>
}

export default CruiseSearchTileBundleCards
